import React, { useState, useEffect, useRef, useCallback } from "react"
import MenuGroup from "aldoo-ra/MenuGroup"
import Icon from "aldoo-ra/Icon"
import Value from "aldoo-ra/Value"
import { twMerge } from "tailwind-merge"

const ArrowMenuMobile = ({
  menuGroupName = "PlanCategories",
  menuData = null, // External data array for programmatic usage
  onSelectionChange,
  preProcessItem,
  itemRender,
  onData,
  styleOverrides = {},
  initialSelectedIndex = 0,
  defaultSelected = null,
  selectedIndex: externalSelectedIndex = null, // Controlled selected index from parent
  enableScrollNavigation = true,
  hiddenItems = [],
  refreshKey = 0,
  interactive = true, // Control whether menu is interactive (swipeable/clickable)
  fixedPosition = true, // Control whether menu has fixed positioning
  showArrow = true, // false: no pointer arrow and no room for it — the strip is the items' height
  arrowSrc = null, // Static image of the pointer arrow, used where the CMS has no "arrow_menu_mobile" icon
  arrowClassName = "w-auto h-[45px]",
  className = "", // merged into the root's classes
}) => {
  const [internalSelectedIndex, setInternalSelectedIndex] = useState(initialSelectedIndex)

  // Use external index if provided (controlled mode), otherwise use internal state
  const selectedIndex = externalSelectedIndex !== null ? externalSelectedIndex : internalSelectedIndex
  const setSelectedIndex = externalSelectedIndex !== null ? () => {} : setInternalSelectedIndex
  const [menuItemsLoaded, setMenuItemsLoaded] = useState(false)
  const [isTouchActive, setIsTouchActive] = useState(false)
  const [arrowPosition, setArrowPosition] = Value("arrowPosition", null)
  const scrollContainerRef = useRef(null)
  const menuItems = useRef([])
  const itemRefs = useRef([])
  const animationFrameRef = useRef(null)
  const isAnimatingRef = useRef(false)
  const touchStartX = useRef(0)
  const lastTouchX = useRef(0)
  const touchStartTime = useRef(0)
  const velocityRef = useRef(0)

  const SWIPE_THRESHOLD = 30
  const VELOCITY_THRESHOLD = 0.3
  const SWIPE_TIME_THRESHOLD = 350
  const SNAP_ANIMATION_DURATION = 250

  // Initialize menu items from external data if provided
  useEffect(() => {
    if (menuData && Array.isArray(menuData)) {
      menuItems.current = menuData

      // Set initial selection if defaultSelected is provided (only in uncontrolled mode)
      if (externalSelectedIndex === null && defaultSelected !== null && defaultSelected !== undefined) {
        const index = menuData.findIndex(
          (item) => item.id === defaultSelected || item.content?.options?.id === defaultSelected
        )
        if (index !== -1) {
          setInternalSelectedIndex(index)
        }
      }

      setMenuItemsLoaded(true)
    }
  }, [menuData, defaultSelected, externalSelectedIndex])


  const getItemRef = useCallback((index) => {
    if (!itemRefs.current[index]) itemRefs.current[index] = React.createRef()
    return itemRefs.current[index]
  }, [])

  const getSelectedIndexFromURL = useCallback(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const planParam = urlParams.get("plans")
    if (planParam && menuItems.current.length) {
      return (
        menuItems.current.findIndex(
          (item) => item.content?.options?.id === planParam
        ) || 0
      )
    }
    return initialSelectedIndex
  }, [initialSelectedIndex])

  const updateURL = useCallback((sectionId) => {
    if (!sectionId) return
    const params = new URLSearchParams(window.location.search)
    params.set("plans", sectionId)
    window.history.pushState({}, "", `/pricing?${params.toString()}`)
  }, [])

  const animateScroll = useCallback(
    (targetScroll, startScroll) => {
      if (!scrollContainerRef.current) return

      // Always cancel any existing animation
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
        animationFrameRef.current = null
      }

      isAnimatingRef.current = true
      const startTime = performance.now()

      const animate = (currentTime) => {
        const elapsed = currentTime - startTime
        const progress = Math.min(elapsed / SNAP_ANIMATION_DURATION, 1)
        const easeProgress = 1 - Math.pow(1 - progress, 3) // Cubic ease-out

        if (scrollContainerRef.current) {
          const newScrollLeft =
            startScroll + (targetScroll - startScroll) * easeProgress
          scrollContainerRef.current.scrollLeft = newScrollLeft

          if (progress >= 1) {
            setArrowPosition(newScrollLeft)
            isAnimatingRef.current = false
            animationFrameRef.current = null
            return // End animation when complete
          }
        } else {
          // Scroll container no longer exists
          isAnimatingRef.current = false
          animationFrameRef.current = null
          return // End animation early
        }

        animationFrameRef.current = requestAnimationFrame(animate)
      }

      animationFrameRef.current = requestAnimationFrame(animate)

      // Safeguard: Ensure animation state is reset after max duration
      setTimeout(() => {
        if (isAnimatingRef.current) {
          isAnimatingRef.current = false
          if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current)
            animationFrameRef.current = null
          }
        }
      }, SNAP_ANIMATION_DURATION + 100) // Add buffer to max duration
    },
    [setArrowPosition]
  )

  const centerSelectedItem = useCallback(() => {
    if (
      selectedIndex < 0 ||
      !scrollContainerRef.current ||
      !menuItems.current.length ||
      isAnimatingRef.current
    )
      return

    const selectedItem = itemRefs.current[selectedIndex]?.current
    if (!selectedItem) return

    const viewportCenter = window.innerWidth / 2

    // Get the item's absolute position in the document
    const itemRect = selectedItem.getBoundingClientRect()
    const itemCenterInViewport = itemRect.left + itemRect.width / 2

    // Calculate how much we need to scroll to center the item
    // If item is to the right of center, scroll right (positive)
    // If item is to the left of center, scroll left (negative)
    const scrollAdjustment = itemCenterInViewport - viewportCenter

    const targetScroll = scrollContainerRef.current.scrollLeft + scrollAdjustment
    const startScroll = scrollContainerRef.current.scrollLeft

    animateScroll(targetScroll, startScroll)
  }, [selectedIndex, animateScroll])

  // Watch for external selectedIndex changes and center the item
  useEffect(() => {
    if (externalSelectedIndex !== null && menuItemsLoaded && !isAnimatingRef.current) {
      centerSelectedItem()
    }
  }, [externalSelectedIndex, menuItemsLoaded, centerSelectedItem])

  const findNearestItem = useCallback(() => {
    if (!scrollContainerRef.current || !itemRefs.current.length)
      return selectedIndex

    const viewportCenter = window.innerWidth / 2
    const currentScroll = scrollContainerRef.current.scrollLeft

    let nearestIndex = 0
    let minDistance = Infinity

    itemRefs.current.forEach((ref, index) => {
      const item = ref?.current
      if (!item) return

      const rect = item.getBoundingClientRect()
      const itemCenter = rect.left + rect.width / 2
      const distance = Math.abs(itemCenter - viewportCenter)

      if (distance < minDistance) {
        minDistance = distance
        nearestIndex = index
      }
    })

    return nearestIndex
  }, [selectedIndex])

  const handleSelection = useCallback(
    (index) => {
      if (
        !interactive ||
        index === selectedIndex ||
        index < 0 ||
        index >= menuItems.current.length ||
        isAnimatingRef.current
      )
        return

      setSelectedIndex(index)
      if (enableScrollNavigation) {
        const sectionId = menuItems.current[index]?.content?.options?.id
        if (sectionId) {
          const section = document.querySelector(
            `[data-category="${sectionId}"]`
          )
          section?.scrollIntoView({ behavior: "smooth", block: "start" })
          updateURL(sectionId)
        }
      }

      if (onSelectionChange) {
        onSelectionChange(index, menuItems.current[index])
      }
    },
    [selectedIndex, enableScrollNavigation, onSelectionChange, updateURL]
  )

  const handleTouchStart = useCallback((e) => {
    if (!scrollContainerRef.current) return
    setIsTouchActive(true)
    const touch = e.touches[0]
    touchStartX.current = touch.clientX
    lastTouchX.current = touch.clientX
    touchStartTime.current = Date.now()
    velocityRef.current = 0
    scrollContainerRef.current.dataset.startScroll =
      scrollContainerRef.current.scrollLeft
  }, [])

  const handleTouchMove = useCallback(
    (e) => {
      if (!isTouchActive || !scrollContainerRef.current) return

      const touch = e.touches[0]
      const currentX = touch.clientX
      const timeDelta = Date.now() - touchStartTime.current

      if (timeDelta > 0) {
        const instantVelocity = (currentX - lastTouchX.current) / timeDelta
        velocityRef.current = 0.8 * velocityRef.current + 0.2 * instantVelocity
      }
      lastTouchX.current = currentX

      const startScroll = parseFloat(
        scrollContainerRef.current.dataset.startScroll
      )
      const deltaX = touch.clientX - touchStartX.current
      const newScrollLeft = startScroll - deltaX
      scrollContainerRef.current.scrollLeft = newScrollLeft
      setArrowPosition(newScrollLeft) // Update during drag
    },
    [isTouchActive, setArrowPosition]
  )

  const handleTouchEnd = useCallback(() => {
    if (!scrollContainerRef.current) return
    setIsTouchActive(false)

    const touchDuration = Date.now() - touchStartTime.current
    const touchDistance = lastTouchX.current - touchStartX.current
    const absDistance = Math.abs(touchDistance)

    // Small movements with short duration = tap (let click handler work)
    if (absDistance < 10 && touchDuration < 200) {
      return
    }

    // Reset animation state if stuck
    if (isAnimatingRef.current && Date.now() - touchStartTime.current > 1000) {
      isAnimatingRef.current = false
    }

    const isSwipe =
      (absDistance > SWIPE_THRESHOLD && touchDuration < SWIPE_TIME_THRESHOLD) ||
      Math.abs(velocityRef.current) > VELOCITY_THRESHOLD

    if (isSwipe) {
      const direction = touchDistance > 0 ? -1 : 1
      const newIndex = Math.min(
        Math.max(selectedIndex + direction, 0),
        menuItems.current.length - 1
      )
      handleSelection(newIndex)
    } else {
      // Force snap to nearest for non-swipe, even if already selected
      const nearestIndex = findNearestItem()
      const selectedItem = itemRefs.current[nearestIndex]?.current

      if (selectedItem) {
        const viewportCenter = window.innerWidth / 2
        const itemRect = selectedItem.getBoundingClientRect()
        const targetScroll =
          scrollContainerRef.current.scrollLeft +
          (itemRect.left + itemRect.width / 2 - viewportCenter)

        // Always animate to ensure proper snapping
        animateScroll(targetScroll, scrollContainerRef.current.scrollLeft)

        // Only update selection if different
        if (nearestIndex !== selectedIndex) {
          setSelectedIndex(nearestIndex)

          if (enableScrollNavigation) {
            const sectionId =
              menuItems.current[nearestIndex]?.content?.options?.id
            if (sectionId) {
              const section = document.querySelector(
                `[data-category="${sectionId}"]`
              )
              section?.scrollIntoView({ behavior: "smooth", block: "start" })
              updateURL(sectionId)
            }
          }

          if (onSelectionChange) {
            onSelectionChange(nearestIndex, menuItems.current[nearestIndex])
          }
        }
      }
    }
  }, [
    selectedIndex,
    findNearestItem,
    animateScroll,
    enableScrollNavigation,
    updateURL,
    onSelectionChange,
    handleSelection,
  ])

  // Add cleanup effect to prevent memory leaks
  useEffect(() => {
    // Cleanup function to ensure references are reset
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
        animationFrameRef.current = null
      }
      isAnimatingRef.current = false
    }
  }, [])

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current
    if (!scrollContainer || !interactive) return

    // Remove any existing listeners first to prevent duplicates
    scrollContainer.removeEventListener("touchstart", handleTouchStart)
    scrollContainer.removeEventListener("touchmove", handleTouchMove)
    scrollContainer.removeEventListener("touchend", handleTouchEnd)

    // Add current listeners only if interactive
    scrollContainer.addEventListener("touchstart", handleTouchStart, {
      passive: true,
    })
    scrollContainer.addEventListener("touchmove", handleTouchMove, {
      passive: true,
    })
    scrollContainer.addEventListener("touchend", handleTouchEnd, {
      passive: true,
    })

    // Set initial scroll position from stored value if available
    if (
      arrowPosition !== null &&
      scrollContainerRef.current &&
      !isAnimatingRef.current
    ) {
      scrollContainerRef.current.scrollLeft = arrowPosition
    }

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener("touchstart", handleTouchStart)
        scrollContainer.removeEventListener("touchmove", handleTouchMove)
        scrollContainer.removeEventListener("touchend", handleTouchEnd)
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
        animationFrameRef.current = null
      }
      isAnimatingRef.current = false
    }
  }, [handleTouchStart, handleTouchMove, handleTouchEnd, arrowPosition, interactive])

  useEffect(() => {
    // Only sync with URL in uncontrolled mode
    if (externalSelectedIndex === null && menuItemsLoaded && !isAnimatingRef.current) {
      const urlIndex = getSelectedIndexFromURL()
      setInternalSelectedIndex(urlIndex)
      // Delay to allow flex layout to settle and set initial scroll position without animation
      setTimeout(() => {
        const selectedItem = itemRefs.current[urlIndex]?.current
        if (selectedItem && scrollContainerRef.current) {
          const viewportCenter = window.innerWidth / 2

          // Get the item's absolute position in the viewport
          const itemRect = selectedItem.getBoundingClientRect()
          const itemCenterInViewport = itemRect.left + itemRect.width / 2

          // Calculate scroll adjustment needed to center the item
          const scrollAdjustment = itemCenterInViewport - viewportCenter
          const targetScroll = scrollContainerRef.current.scrollLeft + scrollAdjustment

          // Set scroll position directly without animation for initial load
          scrollContainerRef.current.scrollLeft = targetScroll
          setArrowPosition(targetScroll)
        }
      }, 100)
    }
  }, [menuItemsLoaded, getSelectedIndexFromURL, setArrowPosition, externalSelectedIndex])

  useEffect(() => {
    // Center whenever selectedIndex changes (works for both controlled and uncontrolled)
    if (menuItemsLoaded && !isAnimatingRef.current) {
      // Small delay to allow flex layout to update
      const timer = setTimeout(centerSelectedItem, 50)
      return () => clearTimeout(timer)
    }
  }, [selectedIndex, centerSelectedItem, menuItemsLoaded])

  // Recenter when menu data changes (for controlled mode with external data)
  useEffect(() => {
    if (menuData && externalSelectedIndex !== null && scrollContainerRef.current) {
      const centerItem = () => {
        const selectedItem = itemRefs.current[externalSelectedIndex]?.current
        if (selectedItem && scrollContainerRef.current && !isAnimatingRef.current) {
          const viewportCenter = window.innerWidth / 2
          const itemRect = selectedItem.getBoundingClientRect()
          const itemCenterInViewport = itemRect.left + itemRect.width / 2
          const scrollAdjustment = itemCenterInViewport - viewportCenter

          if (Math.abs(scrollAdjustment) > 1) {
            scrollContainerRef.current.scrollLeft += scrollAdjustment
            setArrowPosition(scrollContainerRef.current.scrollLeft)
          }
        }
      }

      // Multiple attempts to ensure centering happens after render and animations
      const timers = [100, 200, 300].map(delay =>
        setTimeout(centerItem, delay)
      )

      return () => timers.forEach(timer => clearTimeout(timer))
    }
  }, [menuData, externalSelectedIndex, setArrowPosition])

  useEffect(() => {
    // Only enable scroll navigation in uncontrolled mode
    if (!enableScrollNavigation || !menuItemsLoaded || externalSelectedIndex !== null) return

    const handleScroll = () => {
      if (isAnimatingRef.current) return

      const viewportMiddle = window.scrollY + window.innerHeight / 2
      let closestIndex = selectedIndex
      let minDistance = Infinity

      menuItems.current.forEach((item, index) => {
        const section = document.querySelector(
          `[data-category="${item.content?.options?.id}"]`
        )
        if (!section) return

        const rect = section.getBoundingClientRect()
        const sectionMiddle = window.scrollY + rect.top + rect.height / 2
        const distance = Math.abs(sectionMiddle - viewportMiddle)

        if (distance < minDistance) {
          minDistance = distance
          closestIndex = index
        }
      })

      if (closestIndex !== selectedIndex) {
        setInternalSelectedIndex(closestIndex)
        updateURL(menuItems.current[closestIndex]?.content?.options?.id)
      }
    }

    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [selectedIndex, enableScrollNavigation, menuItemsLoaded, updateURL, externalSelectedIndex])

  return (
    <div className={twMerge(fixedPosition ? "fixed top-0 md:top-[0px] lg:top-[80px] left-0 w-full z-10 bg-white shadow-md" : "relative w-full bg-white shadow-md", className)}>
      <div className={showArrow ? "relative h-[80px] mx-auto" : "relative h-[56px] mx-auto"}>
        {showArrow && (
          <div className="absolute -top-5 left-0 w-full flex justify-center pointer-events-none z-20">
            <Icon id="arrow_menu_mobile" fallbackSrc={arrowSrc} className={arrowClassName} />
          </div>
        )}
        <div className={showArrow ? "relative pt-[35px]" : "relative pt-2"}>
          <div
            ref={scrollContainerRef}
            className={`flex items-start overflow-x-auto w-full ${interactive ? 'touch-pan-x' : 'pointer-events-none'} [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]`}
          >
            <div className="flex">
              <div style={{ width: '50vw', flexShrink: 0 }} />
              {menuData ? (
                // Render external data directly
                menuData.map((item, index) => {
                  const isSelected = selectedIndex === index
                  const itemContent = itemRender ? (
                    itemRender(item, index, isSelected)
                  ) : (
                    <div
                      className={`min-w-[150px] h-[40px] flex items-center justify-center px-0 transition-all duration-300
                        ${isSelected ? "text-primary font-bold scale-105" : "text-gray_2 scale-100"}`}
                    >
                      <span className="text-base text-center">
                        {item.label || item.name}
                      </span>
                    </div>
                  )

                  return (
                    <div
                      key={item.id || index}
                      ref={getItemRef(index)}
                      onClick={() => handleSelection(index)}
                      className="snap-center menu-item relative inline-block"
                      style={{ transition: "transform 0.3s ease-in-out" }}
                    >
                      {itemContent}
                    </div>
                  )
                })
              ) : (
                // Render MenuGroup for CMS data
                <MenuGroup
                key={`menu-${refreshKey}-${JSON.stringify(hiddenItems)}`}
                name={menuGroupName}
                skipRootItem
                renderAs="hlist"
                preProcessItem={preProcessItem}
                onData={(payload) => {
                  const items = payload?.data[0]?.content?.items || []
                  
                  // Filter out hidden items
                  const filteredItems = items.filter(item => 
                    !hiddenItems.includes(item.content?.options?.id)
                  )
                  
                  menuItems.current = filteredItems
                  
                  // Set initial selection if defaultSelected is provided and not filtered out
                  if (defaultSelected) {
                    const index = filteredItems.findIndex(
                      (item) => item.content?.options?.id === defaultSelected
                    )
                    
                    if (index !== -1) {
                      setSelectedIndex(index)
                    }
                  }
                  
                  setMenuItemsLoaded(true)
                  onData?.(payload)
                }}
                menuItemRender={(item, index) => {
                  const categoryId = item.options?.id
                  
                  // Check if this item should be hidden
                  if (hiddenItems.includes(categoryId)) {
                    return null
                  }

                  const isSelected = selectedIndex === index
                  const itemContent = itemRender ? (
                    itemRender(item, index, isSelected)
                  ) : (
                    <div
                      className={`min-w-[150px] h-[40px] flex items-center justify-center px-0 transition-all duration-300
                        ${
                          isSelected
                            ? "text-primary font-bold scale-105"
                            : "text-gray_2 scale-100"
                        }`}
                    >
                      <span className="text-base text-center">
                        {item.label}
                      </span>
                    </div>
                  )

                  return (
                    <div
                      key={index}
                      ref={getItemRef(index)}
                      onClick={() => handleSelection(index)}
                      className="snap-center menu-item relative inline-block"
                      style={{ transition: "transform 0.3s ease-in-out" }}
                    >
                      {itemContent}
                    </div>
                  )
                }}
              />
              )}
              <div style={{ width: '50vw', flexShrink: 0 }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ArrowMenuMobile
